import { useParams } from 'react-router-dom';
import Header from "../components/core/Header";
import Footer from "../components/core/Footer";
import styles from "./ProjectPage.module.css";
import typography from '../theme/typography';
import colors from '../theme/colors';
import usePageMeta from '../utils/usePageMeta';
import pagesSeo from '../data/pagesSeo.json';

import sintelImage from '../assets/images/projects/Project=sintel.png';
import sibylImage from '../assets/images/projects/Project=sibyl.png';
import deepMiningImage from '../assets/images/projects/Project=deep-mining.png';
import featureHubImage from '../assets/images/projects/Project=feature-hub.png';
import atmImage from '../assets/images/projects/Project=atm.png';
import senseMLImage from '../assets/images/projects/Project=senseML.png';
import traneImage from '../assets/images/projects/Project=trane.png';
import adelImage from '../assets/images/projects/Project=adel.png';
import sdvImage from '../assets/images/projects/Project=sdv.png';

const ProjectPage = () => {
  const { projectId } = useParams();

  usePageMeta(
    pagesSeo['project-' + projectId] || {
      title: 'Projects | DAI Group',
      description: pagesSeo.projects.description,
      path: '/projects/' + projectId,
      breadcrumbName: 'Projects',
    }
  );

  const projectsData = {
    'sintel': {
      image: sintelImage,
      eyebrow: 'Sintel',
      eyebrowColor: colors.dark,
      title: 'Sintel',
      description: 'Sintel is an ecosystem for time series tasks including, time series featurization, anomaly detection, forecasting, and predictive maintenance. Sintel provides the ability to customize tools for particular domains such as predictive maintenance for wind energy industry.',
      githubLink: 'https://github.com/sintel-dev/Sintel',
      contributors: [
        'Sarah Alnegheimish',
        'Dongyu Liu',
        'Carles Sala',
        'Laure Berti-Equille',
        'Kalyan Veeramachaneni'
      ],
      publications: [
        {
          title: 'Sintel: A Machine Learning Framework to Extract Insights from Signals',
          authors: 'Sarah Alnegheimish, Dongyu Liu, Carles Sala, Laure Berti-Equille, and Kalyan Veeramachaneni.',
          venue: 'IEEE International Conference on Data Science and Advanced Analytics, Sydney, Australia, October, 2021.'
        }
      ]
    },
    'sibyl': {
      image: sibylImage,
      eyebrow: 'Sibyl',
      eyebrowColor: colors.dark,
      title: 'Sibyl',
      description: 'Sibyl is a highly-configurable system for developing usable ML applications for any domain. Sibyl creates explanations of ML models that are rapidly understandable to audiences of all kinds, including those without ML experience.',
      githubLink: 'https://github.com/sibyl-dev/sibyl',
      contributors: [
        'Alexandra Zytek',
        'Dongyu Liu',
        'Kalyan Veeramachaneni'
      ],
      publications: []
    },
    'deep-mining': {
      image: deepMiningImage,
      eyebrow: 'Deep Mining',
      eyebrowColor: colors.dark,
      title: 'Deep Mining',
      description: 'A large part of a data scientist\'s work involves tuning hyperparameters—making decisions about what happens to the data during all different steps of the process. Such tuning takes a significant amount of time and effort, and small differences in hyperparameters can significantly affect the performance of a particular pipeline. Through our system called "Deep Mining," we seek to automatically tune the entire data processing pipeline—not just the classification algorithms as in the previous project - ATM. This involves standardizing the pipeline abstractions, and building and testing several hyperparameter selection and optimization methods. Since the earlier stages of the pipeline can be computationally expensive, our focus is also on determining the most efficient distribution strategy, and a sampling-based performance estimator.',
      githubLink: 'https://github.com/HDI-Project/ATM',
      contributors: [
        'William Xue',
        'Laura Gustafson',
        'Akshay Ravikumar',
        'Alfredo Cuesta-Infante',
        'Alec Anderson (2016-17)',
        'Sebastien Dubois (2015)'
      ],
      publications: [
        {
          title: 'Sample, Estimate, Tune: Scaling Bayesian Auto-Tuning of Data Science Pipelines (PDF)',
          authors: 'Alec Anderson, Sebastien Dubois, Alfredo Cuesta-Infante and Kalyan Veeramachaneni.',
          venue: 'IEEE International Conference on Data Science and Advance Analytics, Tokyo, Japan, October, 2017.'
        },
        {
          title: 'Deep Mining: Scaling Bayesian Auto-tuning of Data Science Pipelines (PDF)',
          authors: 'Alec W. Anderson, M.E. thesis, MIT Dept of EECS, August 2017. Advisor: Kalyan Veeramachaneni.'
        }
      ]
    },
    'feature-hub': {
      image: featureHubImage,
      eyebrow: 'Feature Hub',
      eyebrowColor: colors.dark,
      title: 'Feature Hub',
      description: 'A tool that makes feature engineering easier, faster, and more effective by collaboratively crowdsourcing feature creation via an open-source web notebook. FeatureHub gathers, tests, collates and extracts features submitted by users, and uses them to train relevant machine learning models.',
      githubLink: 'https://github.com/HDI-Project/FeatureHub',
      contributors: [],
      publications: []
    },
    'atm': {
      image: atmImage,
      eyebrow: 'Auto Tune Models (ATM)',
      eyebrowColor: colors.dark,
      title: 'Auto Tune Models (ATM)',
      description: 'A cloud-based modeling system providing an easier, faster way to select the best model for a given prediction problem. ATM performs bandit-based and Gaussian process learning to decide among methodologies, pinpointing which parameters and hyperparameters to use.',
      githubLink: 'https://github.com/HDI-Project/ATM',
      contributors: [],
      publications: []
    },
    'sense-ml': {
      image: senseMLImage,
      eyebrow: 'Sense ML',
      eyebrowColor: colors.dark,
      title: 'Sense ML',
      description: 'A toolset-based master-worker architecture that can process IOT data from segmentation all the way through the formation of a machine learning example. In the SenseML platform, some steps—such as pre-processing—are automated, while others are surfaced strategically to allow data scientists to input their expertise.',
      githubLink: 'https://github.com/HDI-Project/SenseML',
      contributors: [],
      publications: []
    },
    'trane': {
      image: traneImage,
      eyebrow: 'Trane',
      eyebrowColor: colors.dark,
      title: 'Trane',
      description: 'A language for describing prediction problems over relational datasets, allowing data scientists to specify problems in that language. TRANE automatically generates prediction problems for temporal datasets and produces labels for supervised learning. Its goal is to streamline the machine learning problem-solving process.',
      githubLink: 'https://github.com/HDI-Project/Trane',
      contributors: [],
      publications: []
    },
    'adel': {
      image: adelImage,
      eyebrow: 'Automatic Data Elements Linking (ADEL)',
      eyebrowColor: colors.dark,
      title: 'Automatic Data Elements Linking (ADEL)',
      description: 'A tool that generates a relational data schema and identifies other salient properties for a data model without meta information. It detects the type of each data field, possible Primary Keys for each row, and relationships between different data entities.',
      githubLink: 'https://github.com/HDI-Project/ADEL',
      contributors: [],
      publications: []
    },
    'sdv': {
      image: sdvImage,
      eyebrow: 'Synthetic Data Vault (SDV)',
      eyebrowColor: colors.dark,
      title: 'Synthetic Data Vault (SDV)',
      description: 'SDV enables data scientists to sidestep data-sharing concerns and expand the pool of possible problem participants by generating synthetic data. By learning a generative model that accounts for dependence and relationships, the SDV creates new data that resembles the original set statistically, formally, and structurally.',
      githubLink: 'https://github.com/sdv-dev/SDV',
      contributors: [],
      publications: []
    }
  };

  const project = projectsData[projectId];

  if (!project) {
    return (
      <div>
        <Header />
        <div className={styles.container}>
          <h1>Project not found</h1>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Header />

      <section className={styles.projectSection}>
        <div className={styles.container}>
          {project.image && (
            <div className={styles.projectImageWrapper}>
              <img src={project.image} alt={project.title} className={styles.projectImage} />
            </div>
          )}

          <h1
            className="page-primary-title"
            style={{
              ...typography.heading.large,
              color: colors.dark,
              margin: 0,
              marginBottom: '32px'
            }}
          >
            {project.title}
          </h1>

          <div className={styles.contentWrapper}>
            <div className={styles.mainContent}>
              <p
                style={{
                  ...typography.body.large.regular,
                  color: colors.gray[800],
                  lineHeight: '1.8',
                  margin: 0,
                  marginBottom: '48px'
                }}
              >
                {project.description}
              </p>

              {project.publications && project.publications.length > 0 && (
                <div className={styles.publicationsSection}>
                  <h2
                    style={{
                      ...typography.heading.xsmall,
                      color: colors.dark,
                      margin: 0,
                      marginBottom: '24px'
                    }}
                  >
                    Publications
                  </h2>
                  {project.publications.map((pub, index) => (
                    <div key={index} className={styles.publication}>
                      <h3
                        style={{
                          ...typography.body.large.semibold,
                          color: colors.dark,
                          margin: 0,
                          marginBottom: '8px',
                          textDecoration: 'underline'
                        }}
                      >
                        {pub.title}
                      </h3>
                      <p
                        style={{
                          ...typography.body.medium.regular,
                          color: colors.gray[700],
                          margin: 0,
                          lineHeight: '1.6'
                        }}
                      >
                        {pub.authors} {pub.venue}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.sidebar}>
              {project.githubLink && (
                <div className={styles.sidebarSection}>
                  <a
                    href={project.githubLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.githubLink}
                  >
                    <svg
                      height="32"
                      width="32"
                      viewBox="0 0 16 16"
                      fill="currentColor"
                    >
                      <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z" />
                    </svg>
                  </a>
                </div>
              )}

              {project.contributors && project.contributors.length > 0 && (
                <div className={styles.sidebarSection}>
                  <h3
                    style={{
                      ...typography.body.large.bold,
                      color: colors.dark,
                      margin: 0,
                      marginBottom: '16px'
                    }}
                  >
                    Contributors
                  </h3>
                  <ul className={styles.contributorsList}>
                    {project.contributors.map((contributor, index) => (
                      <li
                        key={index}
                        style={{
                          ...typography.body.medium.regular,
                          color: colors.gray[800]
                        }}
                      >
                        {contributor}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default ProjectPage;
